import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaStickyNote, FaDownload, FaTimes } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Notes.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://uat.definiteassurance.com/api';
const COMPANY = 'Definite';
const ROWS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 400;

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatISODate = (input) => {
  try {
    if (typeof input === 'string') return input.split('T')[0];
    if (input instanceof Date) return input.toISOString().split('T')[0];
    return '--';
  } catch { return '--'; }
};

const formatter = (amount) =>
  Number(amount || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

/** Build smart page numbers with ellipses */
const buildPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('...');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push('...');
  pages.push(total);
  return pages;
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Notes() {
  const navigate = useNavigate();
  const quotationRef = useRef(null);

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsauth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentcode] = useState('');
  const [agentName, setAgentname] = useState('');

  // ── Data ──
  const [allNotes, setAllnotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // ── Modal / Note detail (single object instead of 15 states) ──
  const [modal, setModal] = useState(false);
  const [noteDetail, setNoteDetail] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // ══════════════════════════════════════
  //  AUTH HEADERS
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => {
    const base = { 'Content-Type': 'application/json' };
    return credentials ? { ...base, Authorization: `Basic ${credentials}` } : base;
  }, [credentials]);

  // ══════════════════════════════════════
  //  INITIAL DATA FETCHING
  // ══════════════════════════════════════

  // Credentials
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsauth(true);
      } catch (e) {
        console.error('Failed to get credentials', e);
        toast.error('Authentication failed');
      }
    })();
  }, []);

  // Agent from localStorage
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (user) setAgent(user);
    else navigate('/login');
  }, [navigate]);

  // Agent details
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${agent}`, {
          method: 'GET', headers: authHeaders()
        });
        const data = await res.json();
        setAgentcode(data.OID);
        setAgentname(data.Dealer_name || '');
      } catch (e) {
        console.error('getDealerdetails error', e);
      }
    })();
  }, [agent, isAuth, authHeaders]);

  // Fetch all notes
  useEffect(() => {
    if (!agentCode || !isAuth) return;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API}/getNotes?agent=${agentCode}`, {
          method: 'GET', headers: authHeaders()
        });
        const data = await res.json();
        setAllnotes(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('getNotes error', e);
        toast.error('Failed to load notes');
        setAllnotes([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [agentCode, isAuth, authHeaders]);

  // ══════════════════════════════════════
  //  DEBOUNCED SERVER-SIDE SEARCH
  // ══════════════════════════════════════
  useEffect(() => {
    if (!searchTerm.trim() || !agentCode || !isAuth) {
      // If search cleared, refetch all notes
      if (!searchTerm.trim() && agentCode && isAuth) {
        (async () => {
          try {
            const res = await fetch(`${API}/getNotes?agent=${agentCode}`, {
              method: 'GET', headers: authHeaders()
            });
            const data = await res.json();
            setAllnotes(Array.isArray(data) ? data : []);
          } catch (e) { /* already loaded */ }
        })();
      }
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API}/searchNote?agent=${agentCode}&search=${encodeURIComponent(searchTerm)}`,
          { method: 'GET', headers: authHeaders() }
        );
        const data = await res.json();
        setAllnotes(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('searchNote error', e);
        toast.error('Search failed');
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm, agentCode, isAuth, authHeaders]);

  // Reset to page 1 when search changes
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  // ══════════════════════════════════════
  //  PAGINATED DATA (memoized)
  // ══════════════════════════════════════
  const totalPages = Math.ceil(allNotes.length / ROWS_PER_PAGE);
  const indexOfFirst = (currentPage - 1) * ROWS_PER_PAGE;
  const indexOfLast = indexOfFirst + ROWS_PER_PAGE;
  const currentRows = allNotes.slice(indexOfFirst, indexOfLast);
  const pageNumbers = useMemo(() => buildPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  // ══════════════════════════════════════
  //  MODAL – VIEW NOTE DETAIL
  // ══════════════════════════════════════
  const openNote = useCallback(async (oid) => {
    try {
      const res = await fetch(`${API}/getThisnote?proposal=${oid}`, {
        method: 'GET', headers: authHeaders()
      });
      const data = await res.json();
      setNoteDetail({
        noteNumber: `NW${data.OID}`,
        dateDone: formatISODate(data.DateDone),
        client: `${data.FirstName || ''} ${data.Surname || ''}`.trim(),
        pin: data.DealerPin || '--',
        product: data.ProductDescription || '--',
        sumInsured: data.SumInsured,
        registration: data.Narration || '--',
        basicPremium: (data.BasicPremium || 0) - ((data.TrainingLevy || 0) + (data.PHCF || 0) + (data.StampDuty || 0)),
        trainingLevy: data.TrainingLevy || 0,
        phcf: data.PHCF || 0,
        stampDuty: data.StampDuty || 0,
        grossPremium: data.GrossPremium || 0,
        commence: formatISODate(data.CommencementDate),
        expiry: formatISODate(data.ExpiryDate),
      });
      setModal(true);
    } catch (e) {
      console.error('getThisnote error', e);
      toast.error('Failed to load note details');
    }
  }, [authHeaders]);

  const closeModal = useCallback(() => {
    setModal(false);
    setNoteDetail(null);
  }, []);

  // ══════════════════════════════════════
  //  PDF DOWNLOAD
  // ══════════════════════════════════════
  const downloadPDF = useCallback(async () => {
    const input = quotationRef.current;
    if (!input) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(input, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`debit_note_${noteDetail?.noteNumber || 'note'}.pdf`);
      toast.success('PDF downloaded');
    } catch (e) {
      console.error('PDF generation failed', e);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  }, [noteDetail]);

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  const n = noteDetail; // shorthand for modal template

  return (
    <div className="notes-page">
      <Sidebar />
      <div className="notes-main">
        <Header userName={agentName || agent || 'Agent'} />
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Top bar */}
        <div className="notes-top-row">
          <span className="notes-company">Definite Assurance</span>
          <span className="notes-breadcrumb">Dashboard / Notes</span>
        </div>

        <div className="notes-card">
          <h3><FaStickyNote style={{ marginRight: 8 }} /> Debit Notes</h3>

          {/* Search & stats */}
          <div className="notes-filter-bar">
            <div className="notes-search-wrap">
              <FaSearch className="notes-search-icon" />
              <input
                type="text"
                placeholder="Search by name or registration…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <span className="notes-stat">
              Total: <strong>{allNotes.length}</strong>
            </span>
          </div>

          {/* Loading */}
          {loading && (
            <div className="notes-loading">
              <div className="notes-spinner" />
              <p>Loading notes…</p>
            </div>
          )}

          {/* Empty state */}
          {!loading && allNotes.length === 0 && (
            <div className="notes-empty">
              <FaStickyNote size={32} color="#ccc" style={{ marginBottom: 10 }} />
              <p>{searchTerm ? 'No notes match your search.' : 'No debit notes found.'}</p>
            </div>
          )}

          {/* Table */}
          {!loading && allNotes.length > 0 && (
            <>
              <div className="notes-table-wrap">
                <table className="notes-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Client</th>
                      <th>Product</th>
                      <th>Registration</th>
                      <th>Gross Premium (KES)</th>
                      <th>Amount Paid (KES)</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map((val, index) => (
                      <tr key={val.OID || index}>
                        <td>{indexOfFirst + index + 1}</td>
                        <td>
                          <button
                            className="notes-link-btn"
                            onClick={() => openNote(val.OID)}
                          >
                            {formatISODate(val.DateDone)}
                          </button>
                        </td>
                        <td>{val.FirstName} {val.Surname}</td>
                        <td>{val.ProductDescription}</td>
                        <td>{val.Narration}</td>
                        <td>{formatter(val.GrossPremium)}</td>
                        <td>{formatter(val.PaidAmount)}</td>
                        <td>
                          {Number(val.Status) === 1
                            ? <span className="notes-badge paid">Paid</span>
                            : <span className="notes-badge unpaid">Not Paid</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="notes-pagination">
                <span className="notes-pagination-info">
                  Showing {indexOfFirst + 1} to {Math.min(indexOfLast, allNotes.length)} of {allNotes.length} entries
                </span>
                <div className="notes-pagination-controls">
                  <button
                    className="notes-page-btn"
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  {pageNumbers.map((page, i) =>
                    page === '...'
                      ? <span key={`e${i}`} className="notes-page-ellipsis">…</span>
                      : (
                        <button
                          key={page}
                          className={`notes-page-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      )
                  )}
                  <button
                    className="notes-page-btn"
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ═══════════ NOTE DETAIL MODAL ═══════════ */}
      {modal && n && (
        <div className="notes-modal-overlay" onClick={closeModal}>
          <div className="notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notes-modal-header">
              <h4>Debit Note – {n.noteNumber}</h4>
              <button className="notes-modal-close" onClick={closeModal}><FaTimes /></button>
            </div>

            <div className="notes-modal-body">
              <div ref={quotationRef} className="notes-debit">
                {/* Company header */}
                <div className="notes-debit-header">
                  <img src="/definitelogo.png" alt="Definite Assurance" />
                  <div className="company-info">
                    <h4>Definite Assurance Co. Ltd</h4>
                    <p>P.O BOX 30823 - 00100 NAIROBI</p>
                    <p>0708247247 / 0788247247</p>
                    <p>info@definiteassurance.com</p>
                  </div>
                </div>

                {/* Title */}
                <div className="notes-debit-title">
                  <h4>DEBIT NOTE</h4>
                  <p>{n.dateDone} &nbsp;|&nbsp; {n.noteNumber}</p>
                </div>

                {/* Client info */}
                <div className="notes-debit-client">
                  <p><strong>Client:</strong> {n.client}</p>
                  <p><strong>PIN:</strong> {n.pin}</p>
                  <p><strong>Product:</strong> {n.product}</p>
                  <p><strong>Sum Insured:</strong> {formatter(n.sumInsured)}</p>
                  <p><strong>Registration:</strong> {n.registration}</p>
                </div>

                {/* Items table */}
                <table className="notes-items-table">
                  <thead>
                    <tr>
                      <th>Item Description</th>
                      <th>Amount (KES)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>Basic Premium</td><td>{formatter(n.basicPremium)}</td></tr>
                    <tr><td>Training Levy</td><td>{formatter(n.trainingLevy)}</td></tr>
                    <tr><td>PHCF</td><td>{formatter(n.phcf)}</td></tr>
                    <tr><td>Stamp Duty</td><td>{formatter(n.stampDuty)}</td></tr>
                    <tr className="total-row"><td><strong>Total</strong></td><td><strong>{formatter(n.grossPremium)}</strong></td></tr>
                  </tbody>
                </table>

                {/* Dates */}
                <div className="notes-dates-row">
                  <span><strong>Start:</strong> {n.commence}</span>
                  <span><strong>Expiry:</strong> {n.expiry}</span>
                </div>

                {/* Terms */}
                <div className="notes-terms">
                  <h6>NOTE</h6>
                  <p>1. The Insurance Act Section 156 requires payment of premiums at inception or renewal date of the policy (Cash and Carry).</p>
                  <p>2. Insurance claims shall not be paid if any premiums are outstanding. Access to Healthcare credit facilities will be suspended.</p>
                  <p>3. This document is only for payment purposes and not proof of cover.</p>
                </div>
              </div>
            </div>

            <div className="notes-modal-actions">
              <button
                className="notes-download-btn"
                onClick={downloadPDF}
                disabled={downloading}
              >
                <FaDownload style={{ marginRight: 6 }} />
                {downloading ? 'Generating PDF…' : 'Download as PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
